import React, { useRef, useEffect, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three-stdlib'
import { FenceElement, FenceSystem } from '../contexts/FenceContext'

interface Canvas2DProps {
  elements: FenceElement[]
  selectedElementId: string | null
  onElementSelect: (id: string | null) => void
  fenceSystem: FenceSystem | null
  onReset: () => void
}

const Canvas2D: React.FC<Canvas2DProps> = ({ 
  elements, 
  selectedElementId, 
  onElementSelect, 
  fenceSystem, 
  onReset 
}) => {
  // 🎯 REFERENCJE DO THREE.JS
  const containerRef = useRef<HTMLDivElement>(null)
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const controlsRef = useRef<OrbitControls | null>(null)
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster())
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2())
  
  // 🎯 REFERENCJA DO SCENY 3D Z ThreeJSCanvas3D
  const sceneRef = useRef<THREE.Scene | null>(null)

  // 🎯 INICJALIZACJA KAMERY I RENDERERA
  useEffect(() => {
    if (!containerRef.current) return

    console.log('🎯 Canvas2D: Inicjalizuję kamerę i renderer')

    try {
      // 🎯 KAMERA ORTOGRAFICZNA (widok z góry)
      const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight
      const frustumSize = 10 // 🎯 ZMNIEJSZONE z 20 na 10 - bliższy widok
      const camera = new THREE.OrthographicCamera(
        -frustumSize * aspect, frustumSize * aspect,
        frustumSize, -frustumSize,
        0.1, 1000
      )
      camera.position.set(0, 25, 0) // 🎯 ZMNIEJSZONE z 50 na 25 - bliższy widok
      camera.lookAt(0, 0, 0) // 🎯 PATRZ W DÓŁ
      camera.up.set(0, 0, -1) // 🎯 NAPRAWKA: Ustaw up vector żeby uniknąć skew
      cameraRef.current = camera

      // 🎯 RENDERER - z obsługą błędów i fallback
      let renderer: THREE.WebGLRenderer
      try {
        renderer = new THREE.WebGLRenderer({ 
          antialias: true,
          failIfMajorPerformanceCaveat: false // Pozwól na renderowanie nawet na słabszych urządzeniach
        })
      } catch (error) {
        console.error('❌ Błąd przy tworzeniu WebGL renderera z antialiasing:', error)
        // Fallback: spróbuj bez antialiasing
        try {
          renderer = new THREE.WebGLRenderer({
            antialias: false,
            failIfMajorPerformanceCaveat: false
          })
          console.warn('⚠️ WebGL renderer utworzony bez antialiasing (fallback)')
        } catch (fallbackError) {
          console.error('❌ Błąd przy tworzeniu WebGL renderera (fallback):', fallbackError)
          throw new Error('Nie można utworzyć WebGL renderera. Przeglądarka może nie wspierać WebGL.')
        }
      }
      
      // Sprawdź czy renderer ma poprawny kontekst WebGL
      if (!renderer.getContext()) {
        throw new Error('WebGL context nie został utworzony poprawnie.')
      }
      
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    rendererRef.current = renderer

    // 🎯 KONTROLE (proste przesuwanie kamerą bez Shift)
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableRotate = false // 🎯 BEZ ROTACJI (zawsze widok z góry)
    controls.enablePan = true // 🎯 MOŻNA PRZESUWAĆ KAMERĘ
    controls.enableZoom = true // 🎯 MOŻNA PRZYBLIŻAĆ/ODDALAĆ
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.screenSpacePanning = true
    
    // 🎯 TOUCH EVENTS DLA MOBILE
    controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN
    }
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN, // 🎯 LEWY PRZYCISK = PRZESUWANIE
      MIDDLE: THREE.MOUSE.DOLLY, // 🎯 ŚRODKOWY = PRZYBLIŻANIE
      RIGHT: THREE.MOUSE.PAN // 🎯 PRAWY = TEŻ PRZESUWANIE
    }
    
    // 🎯 NAPRAWKA: Ręczna kontrola minimalnej odległości
    controls.addEventListener('change', () => {
      if (camera.position.y < 10) { // 🎯 MINIMALNA WYSOKOŚĆ KAMERY
        camera.position.y = 10
        controls.target.set(camera.position.x, 0, camera.position.z)
      }
    })
    
    controlsRef.current = controls

    // 🎯 DODAJ RENDERER DO KONTENERA
    containerRef.current.appendChild(renderer.domElement)

    // 🎯 POZWÓL WYBIERAĆ PUNKT STARTU TAKŻE W 2D
    const handleClick = (event: MouseEvent) => {
      if (!(window as any).__isSelectingStartPoint) return
      
      // ⚠️ BLOKADA: Nie pozwalaj wybrać punktu startu, jeśli są już elementy w projekcie
      if (elements.length > 0) {
        console.log('⚠️ Nie można wybrać punktu startu - są już elementy w projekcie')
        ;(window as any).__isSelectingStartPoint = false
        return
      }
      
      if (!cameraRef.current || !containerRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current)

      // Płaszczyzna na poziomie Y=0
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
      const intersection = new THREE.Vector3()

      if (raycasterRef.current.ray.intersectPlane(plane, intersection)) {
        const startPoint = { x: intersection.x, y: 0, z: intersection.z }
        ;(window as any).__fenceStartPoint = startPoint
        ;(window as any).__isSelectingStartPoint = false
        console.log('📍 [2D] Wybrano punkt startu ogrodzenia:', startPoint)

        // 🎯 Powiadom React, że wybrano punkt startu
        if (typeof window !== 'undefined' && typeof (window as any).dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('fenceStartPointSelected'))
        }
      }
    }

    renderer.domElement.addEventListener('click', handleClick)

    // 🎯 TOUCH EVENTS DLA MOBILE
    const handleTouchStart = (event: TouchEvent) => {
      event.preventDefault()
      
      const touch = event.touches[0]
      if (!touch || !containerRef.current) return
      
      const rect = containerRef.current.getBoundingClientRect()
      mouseRef.current.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1
      mouseRef.current.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1
      
      raycasterRef.current.setFromCamera(mouseRef.current, camera)
      
      if (sceneRef.current) {
        const intersects = raycasterRef.current.intersectObjects(sceneRef.current.children, true)
        
        if (intersects.length > 0) {
          let fenceElementId: string | null = null
          
          for (let i = 0; i < intersects.length; i++) {
            const clickedObject = intersects[i].object
            
            if (
              clickedObject.type === 'GridHelper' ||
              clickedObject.type === 'AmbientLight' ||
              clickedObject.type === 'DirectionalLight'
            ) {
              continue
            }
            
            if (clickedObject.userData.fenceElementId) {
              fenceElementId = clickedObject.userData.fenceElementId
              break
            }
            
            if (clickedObject.parent && clickedObject.parent.userData.fenceElementId) {
              fenceElementId = clickedObject.parent.userData.fenceElementId
              break
            }
            
            if (clickedObject.parent?.parent && clickedObject.parent.parent.userData.fenceElementId) {
              fenceElementId = clickedObject.parent.parent.userData.fenceElementId
              break
            }
          }
          
          if (fenceElementId) {
            console.log(`🎯 Canvas2D Touch: Wybrano element ${fenceElementId}`)
            onElementSelect(fenceElementId)
          } else {
            console.log('🎯 Canvas2D Touch: Kliknięto w pustą przestrzeń')
            onElementSelect(null)
          }
        }
      }
    }
    
    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault()
    }
    
    const handleTouchEnd = (event: TouchEvent) => {
      event.preventDefault()
    }
    
    // Dodaj touch event listeners
    renderer.domElement.addEventListener('touchstart', handleTouchStart, { passive: false })
    renderer.domElement.addEventListener('touchmove', handleTouchMove, { passive: false })
    renderer.domElement.addEventListener('touchend', handleTouchEnd, { passive: false })

    // 🎯 RESIZE HANDLER
    const handleResize = () => {
      if (!containerRef.current || !camera || !renderer) return
      
      const width = containerRef.current.clientWidth
      const height = containerRef.current.clientHeight
      
      const aspect = width / height
      if (camera instanceof THREE.OrthographicCamera) {
        camera.left = -frustumSize * aspect
        camera.right = frustumSize * aspect
        camera.updateProjectionMatrix()
      }
      
      renderer.setSize(width, height)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (renderer && renderer.domElement) {
        renderer.domElement.removeEventListener('touchstart', handleTouchStart)
        renderer.domElement.removeEventListener('touchmove', handleTouchMove)
        renderer.domElement.removeEventListener('touchend', handleTouchEnd)
        renderer.domElement.removeEventListener('click', handleClick)
        if (containerRef.current && renderer.domElement) {
          containerRef.current.removeChild(renderer.domElement)
        }
        renderer.dispose()
      }
      if (controls) {
        controls.dispose()
      }
    }
    } catch (error) {
      console.error('❌ Canvas2D: Błąd przy inicjalizacji sceny:', error)
      // Wyświetl komunikat błędu użytkownikowi
      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 20px; text-align: center; color: #ef4444;">
            <h2 style="margin-bottom: 10px;">Błąd inicjalizacji WebGL</h2>
            <p style="margin-bottom: 5px;">${error instanceof Error ? error.message : 'Nie można utworzyć kontekstu WebGL.'}</p>
            <p style="font-size: 14px; color: #6b7280; margin-top: 10px;">
              Upewnij się, że Twoja przeglądarka wspiera WebGL i że jest włączony.
            </p>
          </div>
        `
      }
      return
    }
  }, [])

  // 🎯 POBIERZ SCENĘ 3D Z ThreeJSCanvas3D
  useEffect(() => {
    const getScene = () => {
      if ((window as any).getThreeJSScene) {
        const scene = (window as any).getThreeJSScene()
        if (scene) {
          sceneRef.current = scene
          console.log('🎯 Canvas2D: Pobrano scenę 3D z ThreeJSCanvas3D:', scene)
          console.log('🎯 Canvas2D: Scena ma elementów:', scene.children.length)
        }
      }
    }

    // 🎯 SPRÓBUJ POBRAĆ OD RAZU
    getScene()
    
    // 🎯 JESLI NIE MA, CZEKAJ I SPRÓBUJ PONOWNIE
    if (!sceneRef.current) {
      const interval = setInterval(() => {
        getScene()
        if (sceneRef.current) {
          clearInterval(interval)
        }
      }, 100)
      
      return () => clearInterval(interval)
    }
  }, [])

  // 🎯 RENDERUJ SCENĘ 3D Z KAMERĄ Z GÓRY
  useEffect(() => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return

    console.log('🎯 Canvas2D: Renderuję scenę 3D z góry')
    console.log('🎯 Canvas2D: Scena 3D ma elementów:', sceneRef.current.children.length)

    // 🎯 ANIMACJA - RENDERUJ SCENĘ 3D Z KAMERĄ Z GÓRY
    const animate = () => {
      requestAnimationFrame(animate)
      
      if (controlsRef.current) {
        controlsRef.current.update()
      }
      
      // 🎯 RENDERUJ SCENĘ 3D Z KAMERĄ Z GÓRY
      if (rendererRef.current && cameraRef.current && sceneRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current)
      }
    }
    
    animate()
  }, [fenceSystem, elements])

  // 🎯 AUTOMATYCZNE CENTROWANIE NA ELEMENTACH
  useEffect(() => {
    if (elements.length > 0 && cameraRef.current && controlsRef.current) {
      // 🎯 WYŚRODKUJ KAMERĘ NA WSZYSTKICH ELEMENTACH
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity
      let hasValidElements = false
      
      elements.forEach(element => {
        // 🔒 SPRAWDŹ CZY POSITION ISTNIEJE
        if (!element.position) return
        
        hasValidElements = true
        minX = Math.min(minX, element.position.x - (element.width || 0) / 2)
        maxX = Math.max(maxX, element.position.x + (element.width || 0) / 2)
        minZ = Math.min(minZ, element.position.z - (element.height || 0) / 2)
        maxZ = Math.max(maxZ, element.position.z + (element.height || 0) / 2)
      })
      
      // 🎯 JEŚLI NIE MA WAŻNYCH ELEMENTÓW, UŻYJ DOMYŚLNYCH WARTOŚCI
      if (!hasValidElements) {
        minX = -5
        maxX = 5
        minZ = -5
        maxZ = 5
      }
      
      const centerX = (minX + maxX) / 2
      const centerZ = (minZ + maxZ) / 2
      const size = Math.max(maxX - minX, maxZ - minZ) + 10
      
      // 🔒 SPRAWDŹ CZY WARTOŚCI SĄ POPRAWNE
      if (!isFinite(centerX) || !isFinite(centerZ) || !isFinite(size)) {
        console.warn('⚠️ Canvas2D: Nieprawidłowe wartości centrum, pomijam centrowanie')
        return
      }
      
      // 🎯 USTAW KAMERĘ NA CENTRUM ELEMENTÓW
      const currentY = cameraRef.current.position.y
      cameraRef.current.position.set(centerX, currentY, centerZ)
      cameraRef.current.lookAt(centerX, 0, centerZ)
      
      // 🎯 DOSTOSUJ FRUSTUM KAMERY
      const aspect = containerRef.current?.clientWidth && containerRef.current?.clientHeight 
        ? containerRef.current.clientWidth / containerRef.current.clientHeight 
        : 1
      
      if (cameraRef.current instanceof THREE.OrthographicCamera) {
        const frustumSize = size / 2
        
        // 🔒 SPRAWDŹ CZY FRUSTUM SIZE JEST POPRAWNY
        if (!isFinite(frustumSize) || frustumSize <= 0) {
          console.warn('⚠️ Canvas2D: Nieprawidłowy frustumSize:', frustumSize)
          return
        }
        
        cameraRef.current.left = -frustumSize * aspect
        cameraRef.current.right = frustumSize * aspect
        cameraRef.current.top = frustumSize
        cameraRef.current.bottom = -frustumSize
        cameraRef.current.updateProjectionMatrix()
      }
      
      console.log('🎯 Canvas2D: Wycentrowano kamerę na elementach:', { centerX, centerZ, size })
    }
  }, [elements])

  // 🎯 OBSŁUGA KLIKNIĘCIA
  const handleClick = (event: React.MouseEvent) => {
    if (!raycasterRef.current || !mouseRef.current || !cameraRef.current || !sceneRef.current) return

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current)
    const intersects = raycasterRef.current.intersectObjects(sceneRef.current.children, true)

    if (intersects.length > 0) {
      const clickedObject = intersects[0].object
      const elementId = clickedObject.userData.fenceElementId
      if (elementId) {
        console.log('🎯 Canvas2D: Kliknięto element:', elementId)
        onElementSelect(elementId)
      }
    }
  }

  // 🎯 OBSŁUGA USUWANIA
  const handleDelete = (elementId: string) => {
    if (!fenceSystem) return
    
    console.log('🗑️ Canvas2D: Usuwam element:', elementId)
    
    // Usuń z 3D sceny
    if ((window as any).removeFenceElement) {
      ;(window as any).removeFenceElement(elementId)
    }
    
    // Usuń z React state
    onElementSelect(null)
  }

  // 🎯 SKRÓTY KLAWISZOWE
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' && selectedElementId) {
        handleDelete(selectedElementId)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedElementId])

  return (
    <div className="relative w-full h-full bg-gray-50">
      {/* 🎯 PRZEŁĄCZNIK WIDOKÓW */}
      <div className="absolute top-4 right-4 flex space-x-2">
        {/* Przełącznik 2D/3D - IDENTYCZNY jak w ThreeJSCanvas3D */}
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('toggleViewMode'))
          }}
          className="px-3 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm hover:bg-white transition-colors flex items-center space-x-2"
          title="Przełącz na 3D"
        >
          <span className="text-lg">🎨</span>
          <span className="text-sm font-medium text-gray-700">3D</span>
        </button>
        
        {/* Resetuj kamerę - IDENTYCZNY jak w ThreeJSCanvas3D */}
        <button
          onClick={() => {
            // 🎯 Resetuj kamerę do pozycji domyślnej
            if (cameraRef.current && controlsRef.current) {
              cameraRef.current.position.set(0, 25, 0) // 🎯 Zaktualizowane na nową domyślną wysokość
              cameraRef.current.lookAt(0, 0, 0)
              controlsRef.current.target.set(0, 0, 0)
              controlsRef.current.update()
            }
          }}
          className="p-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm hover:bg-white transition-colors"
          title="Resetuj kamerę"
        >
          🎯
        </button>
        
        {/* Przybliż kamerę - IDENTYCZNY jak w ThreeJSCanvas3D */}
        <button
          onClick={() => {
            // 🎯 Przybliż kamerę do wszystkich elementów (dla OrthographicCamera)
            if (cameraRef.current && controlsRef.current && sceneRef.current) {
              const box = new THREE.Box3().setFromObject(sceneRef.current)
              const center = box.getCenter(new THREE.Vector3())
              const size = box.getSize(new THREE.Vector3())
              
              const maxDim = Math.max(size.x, size.z)
              const margin = 10 // Dodaj margines
              
              // 🎯 Dla OrthographicCamera ustawiamy frustum size
              const frustumSize = maxDim + margin
              cameraRef.current.left = -frustumSize / 2
              cameraRef.current.right = frustumSize / 2
              cameraRef.current.top = frustumSize / 2
              cameraRef.current.bottom = -frustumSize / 2
              cameraRef.current.updateProjectionMatrix()
              
              // 🎯 Ustaw pozycję kamery nad środkiem
              cameraRef.current.position.set(center.x, 20, center.z)
              controlsRef.current.target.copy(center)
              controlsRef.current.update()
            }
          }}
          className="p-2 bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 transition-colors"
          title="Przybliż kamerę do elementów"
        >
          🔍
        </button>
      </div>

      {/* 🎯 KONTENER THREE.JS */}
      <div 
        ref={containerRef} 
        className="w-full h-full cursor-crosshair"
        onClick={handleClick}
        data-canvas-2d="true"
      />
    </div>
  )
}

export default Canvas2D
